import type { ResourceInvocationPlan } from '@/types/dag.types';

/**
 * The camelCase aliases a stored invocation plan may carry for its wire
 * fields, matching the orchestration-service read
 * (canonicalizeInvocationPlanMetadata): the wire spelling wins when both are
 * present, and a blank wire value takes the alias.
 */
const PLAN_FIELD_ALIASES = [
  ['planId', 'plan_id'],
  ['resourceId', 'resource_id'],
  ['operationId', 'operation_id'],
  ['operationContractDigest', 'operation_contract_digest'],
] as const;

/**
 * The plan in its snake_case wire spelling (dag.ResourceInvocationPlan), with
 * the camelCase aliases removed. The parameter-plan check is keyed and asked
 * by these fields (validationPlanKey), so an alias left in place keeps the
 * contract digest from the server (agion/Agion#1780). A plan with no alias is
 * returned as is.
 */
export function withInvocationPlanWireFields<T extends ResourceInvocationPlan>(plan: T): T {
  if (!plan || typeof plan !== 'object') return plan;
  const raw = plan as unknown as Record<string, unknown>;
  if (!PLAN_FIELD_ALIASES.some(([alias]) => alias in raw)) return plan;
  const next: Record<string, unknown> = { ...raw };
  for (const [alias, wire] of PLAN_FIELD_ALIASES) {
    if (!(alias in next)) continue;
    const value = next[alias];
    delete next[alias];
    const held = next[wire];
    if ((typeof held !== 'string' || !held.trim()) && typeof value === 'string' && value.trim()) {
      next[wire] = value.trim();
    }
  }
  return next as unknown as T;
}
